// src/components/MatchResults.js
import React, { useState } from 'react';
import {
  Box, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Badge, 
  Button, 
  Image,
  IconButton,
  useDisclosure,
  Tooltip,
  Grid,
  Icon,
  Container,
} from '@chakra-ui/react';
import { 
  SplitSquareVertical, 
  Check,
  ShoppingCart,
  Package,
  Tag,
  Award,
  Palette,
  List,
  Users,
  Star,
  ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CompareDrawer from './CompareDrawer';

export const colorOptions = [
  { value: 'pink', label: 'ピンク系' },
  { value: 'red', label: 'レッド系' },
  { value: 'purple', label: 'パープル系' },
  { value: 'blue', label: 'ブルー系' },
  { value: 'green', label: 'グリーン系' },
  { value: 'brown', label: 'ブラウン系' },
  { value: 'beige', label: 'ベージュ系' },
  { value: 'black', label: 'ブラック系' },
  { value: 'metallic', label: 'シルバー・ゴールド系' }
];

export const featureOptions = [
  { value: 'lightweight', label: '軽量' },
  { value: 'large-capacity', label: '大容量' },
  { value: 'durable', label: '頑丈💪' },
  { value: 'small-child', label: '小柄なお子様におすすめ' },
  { value: 'tablet-pocket', label: 'タブレットポケット' },
  { value: 'clarino', label: 'クラリーノ(人工皮革)' },
  { value: 'cordovan', label: 'コードバン(馬革)' }
];

export const colorSchemeMap = {
  'pink': 'pink',
  'red': 'red',
  'purple': 'purple',
  'blue': 'blue',
  'green': 'green',
  'brown': 'orange',
  'beige': 'yellow',
  'black': 'gray',
  'metallic': 'gray'
};

// 価格帯表示用の関数を追加
const getPriceRangeLabel = (priceRange) => {
  switch(priceRange) {
    case '60k':
      return '6万台のランドセル';
    case '70k':
      return '7万台のランドセル';
    case 'all':
      return '指定なし';
    default:
      return '';
  }
};

const MatchResults = ({ results, onReset }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [compareItems, setCompareItems] = useState([]);
  const navigate = useNavigate();

  const isItemCompared = (item) => {
    return compareItems.some(i => i.name === item.name);
  };

  const addToCompare = (item) => {
    if (compareItems.length >= 3) {
      return;
    }
    if (!isItemCompared(item)) {
      setCompareItems([...compareItems, item]);
    }
  };

  const removeFromCompare = (item) => {
    setCompareItems(compareItems.filter(i => i.name !== item.name));
  };

  if (!results || results.length === 0) {
    return <Text>マッチする結果が見つかりませんでした。</Text>;
  }

  const topThree = results.slice(0, 3);
  const others = results.slice(3);

  const getRankingColor = (index) => {
    switch(index) {
      case 0: return { bg: 'yellow.400', text: 'black' };
      case 1: return { bg: 'gray.300', text: 'black' };
      case 2: return { bg: 'orange.300', text: 'black' };
      default: return { bg: 'gray.100', text: 'gray.600' };
    }
  };

  const handleBack = () => {
    onReset();
    navigate('/');
  };



  
  return (
    <VStack spacing={4} align="stretch">
      <HStack 
        justify="space-between" 
        align="center"
        flexDirection={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
      >
        <HStack spacing={2}>
          <Icon as={Award} color="blue.500" />
          <Heading size="md">マッチング結果</Heading>
        </HStack>
        <HStack 
          spacing={4}
          width={{ base: "100%", md: "auto" }}
        >
          {compareItems.length > 0 && (
            <Button
              leftIcon={<SplitSquareVertical />}
              colorScheme="blue"
              variant="outline"
              onClick={onOpen}
              size="sm"
              flex={{ base: 1, md: "auto" }}
            >
              {compareItems.length}件を比較
            </Button>
          )}
          <Button
            onClick={handleBack}
            leftIcon={<ArrowLeft size={18} />}
            colorScheme="gray"
            variant="ghost"
            size="sm"
            flex={{ base: 1, md: "auto" }}
          >
            条件を変更してもう一度探す
          </Button>
        </HStack>
      </HStack>


    {/* 比較機能の説明バナー */}
    <Box 
      p={4} 
      bg="blue.50" 
      borderRadius="lg" 
      mb={4}
      borderLeft="4px"
      borderColor="blue.400"
    >
      <HStack spacing={3} align="flex-start">
        <SplitSquareVertical 
          size={24} 
          color="blue.500" 
          style={{ marginTop: '2px' }}
        />
        <Box>
          <Text 
            fontWeight="bold" 
            color="blue.700"
            mb={1}
          >
            商品を比較してみましょう！
          </Text>
          <Text 
            fontSize="sm" 
            color="blue.600"
          >
            各商品の右上にある
            <IconButton
              icon={<SplitSquareVertical size={14} />}
              size="xs"
              variant="ghost"
              colorScheme="gray"
              mx={1}
              isDisabled
            />
            ボタンをクリックすると、最大3つまでの商品を詳しく比較できます。
            選んだ商品は画面上部の「○件を比較」ボタンから確認できます。
          </Text>
        </Box>
      </HStack>
    </Box>

      {/* トップ3の表示 */}
      {topThree.map((result, index) => (
        <Box 
          key={index} 
          borderWidth="1px" 
          borderRadius="lg" 
          p={4} 
          bg="white"
          position="relative"
          transition="all 0.2s"
          _hover={{ boxShadow: 'md' }}
        >
          {/* ランキングバッジ */}
          <Box
            position="absolute"
            top="-3"
            left="-3"
            bg={getRankingColor(index).bg}
            color={getRankingColor(index).text}
            borderRadius="full"
            w="40px"
            h="40px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontWeight="bold"
            fontSize="xl"
            boxShadow="md"
          >
            {index + 1}
          </Box>

          <HStack 
            spacing={6} 
            align="flex-start"
            flexDirection={{ base: "column", md: "row" }}
          >
            {/* 左側：商品画像 */}
            <Box 
              flex="1"
              width={{ base: "100%", md: "auto" }}
            >
              <Image 
                src={result.imageUrl} 
                alt={result.name} 
                borderRadius="md"
                maxH="300px"
                objectFit="contain"
                mx="auto"
              />
            </Box>

            {/* 右側：商品情報 */}
            <Box 
              flex="2"
              width={{ base: "100%", md: "auto" }}
            >
              <HStack justify="space-between" align="center" mb={3}>
                <HStack spacing={2}>
                  <Icon as={Package} size={16} color="gray.500" />
                  <Text color="gray.600" fontSize="sm">
                    品番: {result.code}
                  </Text>
                </HStack>
                <Tooltip 
                  label={
                    isItemCompared(result)
                      ? "クリックして比較リストから削除"
                      : compareItems.length >= 3
                      ? "すでに3つの商品を選択しています"
                      : "クリックして比較リストに追加（最大3つまで）"
                  }
                  placement="top"
                  hasArrow
                  bg="blue.600"
                >
                  <IconButton
                    icon={isItemCompared(result) ? <Check /> : <SplitSquareVertical />}
                    variant={isItemCompared(result) ? "solid" : "ghost"}
                    colorScheme={isItemCompared(result) ? "green" : "gray"}
                    size="sm"
                    isDisabled={compareItems.length >= 3 && !isItemCompared(result)}
                    onClick={() => isItemCompared(result) ? removeFromCompare(result) : addToCompare(result)}
                    aria-label={isItemCompared(result) ? "比較から削除" : "比較に追加"}
                  />
                </Tooltip>
              </HStack>

              <VStack align="stretch" spacing={3} mb={4}>
                <Heading size="md">{result.name}</Heading>
                <HStack spacing={2}>
                  <Icon as={Star} color="blue.500" />
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                    マッチ度: {result.matchScore}%
                  </Badge>
                  {isItemCompared(result) && (
                    <Badge colorScheme="green">比較リストに追加済み</Badge>
                  )}
                </HStack>
              </VStack>

              <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={4}>
                <Box>
                  <HStack mb={2}>
                    <Icon as={Palette} size={16} color="gray.500" />
                    <Text fontWeight="bold">カラー:</Text>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {result.colors && result.colors.map(color => (
                      <Badge 
                        key={color} 
                        colorScheme={colorSchemeMap[color]}
                        variant="subtle"
                        px={3}
                        py={1}
                      >
                        {colorOptions.find(opt => opt.value === color)?.label}
                      </Badge>
                    ))}
                  </VStack>
                </Box>

                <Box>
                  <HStack mb={2}>
                    <Icon as={List} size={16} color="gray.500" />
                    <Text fontWeight="bold">特徴:</Text>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {result.features && result.features.map(feature => (
                      <Badge 
                        key={feature} 
                        colorScheme="green" 
                        variant="subtle"
                        px={3}
                        py={1}
                      >
                        {featureOptions.find(opt => opt.value === feature)?.label}
                      </Badge>
                    ))}
                  </VStack>
                </Box>

                <Box>
                  <HStack mb={2}>
                    <Icon as={Tag} size={16} color="gray.500" />
                    <Text fontWeight="bold">価格:</Text>
                  </HStack>
                  <Text fontSize="lg" pl={6}>
                    {result.price.toLocaleString()}円
                    {result.priceRange && (
                      <Badge ml={2} colorScheme="green">
                        {getPriceRangeLabel(result.priceRange)}
                      </Badge>
                    )}
                  </Text>
                </Box>

                <Box>
                  <HStack mb={2}>
                    <Icon as={Users} size={16} color="gray.500" />
                    <Text fontWeight="bold">性別:</Text>
                  </HStack>
                  <Text fontSize="lg" pl={6}>
                    {result.gender === 'boy' ? '男の子' : 
                    result.gender === 'girl' ? '女の子' : 
                    '男女共用'}
                  </Text>
                </Box>
              </Grid>

              <Box textAlign="center">
                {result.purchaseUrl && (
                  <Button 
                    as="a" 
                    href={result.purchaseUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    colorScheme="pink" 
                    width={{ base: "100%", md: "200px" }}
                    leftIcon={<ShoppingCart size={16} />}
                  >
                    購入ページへ
                  </Button>
                )}
              </Box>
            </Box>
          </HStack>
        </Box>
      ))}

  
       {/* その他のおすすめモデル表示 */}
      {others.length > 0 && (
        <Box mt={8}>
          <HStack spacing={2} mb={4}>
            <Icon as={Star} color="gray.500" />
            <Text fontSize="lg" fontWeight="bold">その他のおすすめモデル</Text>
          </HStack>
          <Grid 
            templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }}
            gap={6}
          >
            {others.map((result, index) => (
              <Box 
                key={index}
                borderWidth="1px"
                borderRadius="lg"
                p={4}
                bg="white"
                position="relative"
                transition="all 0.2s"
                _hover={{ boxShadow: 'md' }}
              >
                {/* ランキングバッジ */}
                <Box
                  position="absolute"
                  top="-2"
                  left="-2"
                  bg={getRankingColor(index + 3).bg}
                  color={getRankingColor(index + 3).text}
                  borderRadius="full"
                  w="32px"
                  h="32px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                  fontSize="md"
                  boxShadow="sm"
                >
                  {index + 4}
                </Box>

                <VStack spacing={4} align="stretch">
                  <Box>
                    <Image 
                      src={result.imageUrl} 
                      alt={result.name} 
                      borderRadius="md"
                      maxH="180px"
                      objectFit="contain"
                      mx="auto"
                    />
                  </Box>

                  <VStack align="stretch" spacing={2}>
                    <HStack spacing={2}>
                      <Icon as={Package} size={14} color="gray.500" />
                      <Text fontSize="sm" color="gray.600">
                        品番: {result.code}
                      </Text>
                    </HStack>
                    
                    <Text 
                      fontSize="md" 
                      fontWeight="bold" 
                      noOfLines={2}
                    >
                      {result.name}
                    </Text>
                  </VStack>

                  <VStack align="stretch" spacing={3}>
                    <HStack spacing={2}>
                      <Icon as={Star} color="blue.500" size={14} />
                      <Badge 
                        colorScheme="blue" 
                        fontSize="sm"
                        width="fit-content"
                      >
                        マッチ度: {result.matchScore}%
                      </Badge>
                    </HStack>
                    
                    <HStack spacing={2}>
                      <Icon as={Tag} color="gray.500" size={14} />
                      <Text fontSize="md" fontWeight="bold">
                        {result.price.toLocaleString()}円
                        {result.priceRange && (
                          <Badge ml={2} colorScheme="green" fontSize="sm">
                            {getPriceRangeLabel(result.priceRange)}
                          </Badge>
                        )}
                      </Text>
                    </HStack>
                  </VStack>

                  <Box textAlign="center">
                    <Button
                      as="a"
                      href={result.purchaseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      colorScheme="pink"
                      leftIcon={<ShoppingCart size={14} />}
                      size="sm"
                      width="full"
                    >
                      購入ページへ
                    </Button>
                  </Box>
                </VStack>
              </Box>
            ))}
          </Grid>
        </Box>
      )}

      {/* 下部の「条件を変更」ボタンとフッター */}
      <VStack spacing={{ base: 6, md: 8 }} mt={8} pb={{ base: 6, md: 8 }}>
        <Button
          onClick={handleBack}
          leftIcon={<ArrowLeft size={18} />}
          colorScheme="gray"
          variant="outline"
          size="md"
          width={{ base: "full", md: "300px" }}
        >
          条件を変更してもう一度探す
        </Button>

        <Box 
          w="full" 
          borderTopWidth="1px" 
          pt={{ base: 6, md: 8 }}
        >
          <Container 
            maxW="container.xl"
            px={{ base: 4, md: 0 }}
          >
            <VStack 
              spacing={{ base: 3, md: 4 }} 
              align="center" 
              color="gray.600"
            >
              <HStack spacing={2}>
                <Icon as={Award} />
                <Heading 
                  size={{ base: "sm", md: "md" }} 
                  color="gray.700"
                >
                  ランドセルマッチング
                </Heading>
              </HStack>
              <Text 
                fontSize="sm"
                textAlign="center"
                px={{ base: 4, md: 0 }}
              >
                お子様に最適なランドセルを見つけるお手伝いをいたします
              </Text>
              <Text 
                fontSize="sm" 
                color="gray.500"
                mt={{ base: 2, md: 0 }}
              >
                Copyright© NAAS co., ltd All rights reserved.
              </Text>
            </VStack>
          </Container>
        </Box>
      </VStack>

      <CompareDrawer 
        isOpen={isOpen} 
        onClose={onClose}
        compareItems={compareItems}
        removeFromCompare={removeFromCompare}
      />
    </VStack>
  );
};

export default MatchResults;